import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Activity as ActivityIcon, ArrowUpRight, Eye, Sparkles, Pause, Play, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import MarketingLayout from "@/components/marketing/MarketingLayout";

const BACKEND = BACKEND_URL;
const WS_URL = BACKEND.replace(/^http/, "ws") + "/api/ws/activity";

const SECTOR_CHIP_CLASS = "bg-slate-50 text-slate-700 ring-1 ring-inset ring-slate-200";
const SECTOR_TONE = {
  mortgages:            SECTOR_CHIP_CLASS,
  "business-finance":   SECTOR_CHIP_CLASS,
  "debt-help":          SECTOR_CHIP_CLASS,
  "payday-loans":       SECTOR_CHIP_CLASS,
  "claims-management":  SECTOR_CHIP_CLASS,
  "life-insurance":     SECTOR_CHIP_CLASS,
  "investor-leads":     SECTOR_CHIP_CLASS,
  "wealth-management":  SECTOR_CHIP_CLASS,
  "commercial-finance": SECTOR_CHIP_CLASS,
  "property-investment":SECTOR_CHIP_CLASS,
};

const SECTOR_FILTERS = [
  { slug: "all", label: "All sectors" },
  { slug: "mortgages", label: "Mortgages" },
  { slug: "business-finance", label: "Business Finance" },
  { slug: "debt-help", label: "Debt Help" },
  { slug: "life-insurance", label: "Life Insurance" },
  { slug: "claims-management", label: "Claims" },
  { slug: "wealth-management", label: "Wealth" },
  { slug: "commercial-finance", label: "Commercial" },
];

function formatRelative(ts) {
  if (!ts) return "just now";
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 5) return "just now";
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return d.toLocaleDateString("en-GB");
}

function dedupeById(arr) {
  const seen = new Set();
  return arr.filter((e) => {
    const k = `${e.type}:${e.id}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export default function Activity() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [paused, setPaused] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [tickCount, setTickCount] = useState(0); // forces relative-time re-render
  const wsRef = useRef(null);
  const pausedRef = useRef(false);
  const bufferRef = useRef([]); // events arriving while paused

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    axios.get(`${BACKEND}/api/public/activity?limit=40`).then((r) => {
      if (!cancelled) setEvents(dedupeById(r.data?.events || []));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

    // WebSocket
  useEffect(() => {
    let reconnectTimer = null;
    let mounted = true;

    const connect = () => {
      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => mounted && setWsConnected(true);
        ws.onclose = () => {
          if (!mounted) return;
          setWsConnected(false);
          reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
        ws.onmessage = (e) => {
          if (!mounted) return;
          let payload;
          try { payload = JSON.parse(e.data); } catch { return; }
          if (!payload?.type) return;
          if (pausedRef.current) {
            bufferRef.current.unshift(payload);
            return;
          }
          setEvents((prev) => dedupeById([payload, ...prev]).slice(0, 60));
        };
      } catch (_) {
        reconnectTimer = setTimeout(connect, 3000);
      }
    };

    connect();
    // Keepalive ping — stops ingress/idle timeouts dropping the stream
    const pingId = setInterval(() => {
      try { if (wsRef.current?.readyState === 1) wsRef.current.send("ping"); } catch {}
    }, 25000);
    return () => {
      mounted = false;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      clearInterval(pingId);
      try { wsRef.current?.close(); } catch {}
    };
  }, []);

  // Safety-net poll — every 60s merge any events the socket missed
  useEffect(() => {
    const id = setInterval(() => {
      if (pausedRef.current) return;
      axios.get(`${BACKEND}/api/public/activity?limit=40`).then((r) => {
        const fresh = r.data?.events || [];
        if (!fresh.length) return;
        setEvents((prev) =>
          dedupeById([...fresh, ...prev])
            .sort((a, b) => (b.ts || "").localeCompare(a.ts || ""))
            .slice(0, 60)
        );
      }).catch(() => {});
    }, 60000);
    return () => clearInterval(id);
  }, []);

  // Force re-render every 15s so relative timestamps stay fresh
  useEffect(() => {
    const id = setInterval(() => setTickCount((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);

  // When unpaused, flush the buffer
  const togglePause = () => {
    setPaused((p) => {
      const next = !p;
      if (!next && bufferRef.current.length > 0) {
        setEvents((prev) => dedupeById([...bufferRef.current, ...prev]).slice(0, 60));
        bufferRef.current = [];
      }
      return next;
    });
  };

  const filtered = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.sector === filter)),
    [events, filter]
  );

  // Aggregate counters (computed from current event list)
  const counters = useMemo(() => {
    const total = events.length;
    const qualified = events.filter((e) => e.type === "ai.qualified").length;
    const routed = events.filter((e) => e.type === "lead.routed").length;
    const value = events.reduce((s, e) => s + (e.value || 0), 0);
    return { total, qualified, routed, value };
  }, [events]);

  return (
    <MarketingLayout>
      <section className="relative border-b border-slate-200/80 overflow-hidden bg-gradient-to-br from-white via-slate-50/40 to-white">
        <div className="absolute inset-0 dot-grid dot-grid-fade opacity-60" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-primary/5 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-10">
          <Badge variant="outline" className="rounded-full border-slate-300 bg-white text-slate-700 px-3 py-1" data-testid="activity-live-badge">
            <span className="relative flex items-center justify-center size-2 mr-2">
              <span className={`absolute inline-flex h-full w-full rounded-full ${wsConnected ? "bg-emerald-400 animate-ping" : "bg-amber-400"} opacity-75`} />
              <span className={`relative inline-flex rounded-full size-2 ${wsConnected ? "bg-emerald-500" : "bg-amber-500"}`} />
            </span>
            {wsConnected ? "Live · streaming via WebSocket" : "Reconnecting…"}
          </Badge>
          <h1 className="mt-5 text-5xl sm:text-6xl tracking-[-0.04em] font-semibold text-slate-900 leading-[0.95] max-w-4xl">
            The Connexis routing engine, <span className="gradient-text">live</span>.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            An anonymised window into the Connexis routing engine — watch AI qualification and ping-tree routing happen in real time, streamed the moment each event fires.
          </p>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            <Counter label="Events on screen" value={counters.total} testid="activity-counter-total" />
            <Counter label="AI qualified" value={counters.qualified} testid="activity-counter-qualified" />
            <Counter label="Leads routed" value={counters.routed} testid="activity-counter-routed" />
            <Counter label="Aggregate value" value={`£${counters.value.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`} testid="activity-counter-value" />
          </div>
        </div>
      </section>

      <section className="py-10 border-b border-slate-200/80 sticky top-16 z-30 bg-white/85 backdrop-blur">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide -mx-1 px-1">
            {SECTOR_FILTERS.map((f) => (
              <button
                key={f.slug}
                onClick={() => setFilter(f.slug)}
                data-testid={`activity-filter-${f.slug}`}
                className={`shrink-0 rounded-full text-xs font-medium px-3 py-1.5 transition-all border ${
                  filter === f.slug
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-slate-500 font-mono tabular-nums">
              {filtered.length} event{filtered.length === 1 ? "" : "s"}
              {paused && bufferRef.current.length > 0 ? ` · ${bufferRef.current.length} buffered` : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={togglePause}
              data-testid="activity-pause-btn"
              className="rounded-full border-slate-300"
            >
              {paused ? <><Play className="size-3.5 mr-1" /> Resume</> : <><Pause className="size-3.5 mr-1" /> Pause</>}
            </Button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
          {/* Feed */}
          <div className="lg:col-span-8" data-testid="activity-feed">
            {filtered.length === 0 && (
              <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center">
                <ActivityIcon className="size-8 text-slate-400 mx-auto" />
                <p className="mt-3 text-sm text-slate-600">No events in this filter yet — try widening the filter or refresh.</p>
              </div>
            )}
            <ul className="space-y-3" data-tick={tickCount}>
              {filtered.map((e, i) => (
                <EventCard key={`${e.type}:${e.id}:${i}`} event={e} />
              ))}
            </ul>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="size-4 text-primary" />
                <h3 className="text-sm font-semibold tracking-tight text-slate-900">What you're watching</h3>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Connexis routes thousands of UK financial enquiries every day. This page shows the last ~60 events — both AI qualifications and ping-tree-routed leads — pushed live over WebSocket.
              </p>
              <ul className="mt-4 space-y-2.5 text-xs text-slate-600">
                <li className="flex items-start gap-2"><span className="inline-block mt-1 size-1.5 rounded-full bg-primary shrink-0" /><span><strong className="text-slate-900">AI qualified</strong> — Connexis AI extracted intent + sector + affordability from a live web/voice chat.</span></li>
                <li className="flex items-start gap-2"><span className="inline-block mt-1 size-1.5 rounded-full bg-emerald-500 shrink-0" /><span><strong className="text-slate-900">Lead routed</strong> — an enquiry was matched and delivered to its single exclusive buyer at the fixed sector CPL.</span></li>
                <li className="flex items-start gap-2"><span className="inline-block mt-1 size-1.5 rounded-full bg-slate-400 shrink-0" /><span><strong className="text-slate-900">Anonymised</strong> — first-initial only, city not address, no contact details, no PII.</span></li>
              </ul>
            </div>
            <div className="rounded-2xl bg-slate-900 text-white p-6 shadow-float">
              <h3 className="text-sm font-semibold tracking-tight">Want enquiries like these?</h3>
              <p className="mt-2 text-sm text-slate-300 leading-relaxed">Plug into the same routing infrastructure with a strategy call.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/book-demo"><Button data-testid="activity-cta-demo" className="rounded-full px-4 h-9 bg-white text-slate-900 hover:bg-slate-100">Book a call <ArrowUpRight className="size-3.5" /></Button></Link>
                <Link to="/pricing"><Button data-testid="activity-cta-pricing" variant="outline" className="rounded-full px-4 h-9 border-white/30 text-white hover:bg-white/10">See pricing</Button></Link>
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 text-xs text-slate-600 flex items-start gap-3">
              {wsConnected
                ? <Wifi className="size-4 text-emerald-600 shrink-0 mt-0.5" />
                : <WifiOff className="size-4 text-amber-600 shrink-0 mt-0.5" />}
              <div>
                <div className="font-medium text-slate-900">{wsConnected ? "Live WebSocket connected" : "WebSocket reconnecting…"}</div>
                <div className="text-slate-500 mt-1">{wsConnected ? "New events appear automatically." : "Showing cached events until the stream resumes."}</div>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </MarketingLayout>
  );
}

function Counter({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-soft" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-[-0.02em] text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}

function EventCard({ event }) {
  const hue = SECTOR_TONE[event.sector] || SECTOR_CHIP_CLASS;
  const isAi = event.type === "ai.qualified";
  return (
    <li
      data-testid={`activity-event-${event.id}`}
      className="group relative rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 hover:border-primary/40 hover:shadow-soft transition-all animate-in fade-in slide-in-from-top-2"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 flex flex-col items-center">
          <span className={`inline-grid place-items-center size-10 rounded-full ring-1 ${hue} font-mono text-[11px] font-semibold`}>
            {isAi ? <Sparkles className="size-4" /> : event.initial}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${hue}`}>
              {event.sector_name}
            </span>
            <span className="text-[11px] font-mono text-slate-400 tabular-nums">{formatRelative(event.ts)}</span>
            <span className="text-[11px] font-mono text-slate-400">·</span>
            <span className="text-[11px] text-slate-500">{event.city}</span>
            {!isAi && event.source && (
              <>
                <span className="text-[11px] font-mono text-slate-400">·</span>
                <span className="text-[11px] text-slate-500">{event.source}</span>
              </>
            )}
          </div>
          <div className="mt-1.5 text-sm text-slate-900 leading-relaxed">
            {isAi ? (
              <>
                <span className="text-primary font-medium">AI qualified</span> — "<span className="text-slate-700">{event.intent}</span>"
                {event.winning_buyer && (
                  <> — routed to <span className="text-slate-900 font-medium">{event.winning_buyer}</span></>
                )}
              </>
            ) : (
              <>
                Lead routed for <span className="text-slate-900 font-medium">{event.initial}</span> in {event.city} —{" "}
                <span className="text-slate-700">status</span> <span className="font-medium">{event.status}</span>
              </>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
            {event.value > 0 && (
              <span className="font-mono tabular-nums text-slate-700">£{event.value.toFixed(2)}</span>
            )}
            {isAi && event.confidence > 0 && (
              <span className="font-mono tabular-nums">confidence {event.confidence}%</span>
            )}
            {isAi && event.replay_id && (
              <Link
                to={`/replay/${event.replay_id}`}
                data-testid={`activity-replay-link-${event.replay_id}`}
                className="inline-flex items-center gap-1 text-primary hover:underline font-medium"
              >
                <Eye className="size-3.5" /> Watch replay
              </Link>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
